/**
 * Seção de aprovação do parecer pelo perito (E4.F2a.T02 + T04).
 *
 * Antes da aprovação: botão "Aprovar parecer" + descrição. Após a aprovação
 * (server-side via record.approved_at OU client-side recém-feita), o botão é
 * substituído por uma confirmação ARIA-live com timestamp formatado em
 * pt-BR. Não há fluxo de "desaprovar" — a marca é definitiva nesta versão.
 */
import { useCallback, useState } from 'react';
import { Blueprint, Button, CardKicker } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  AnalysisError,
  approveAnalysis,
  type AnalysisApprovalData,
  type AnalysisErrorReason,
  type AnalysisRecordData,
} from '@/lib/analyses';
import { useApiClient } from '@/lib/api';

function approvalErrorMessage(reason: AnalysisErrorReason): string {
  if (reason === 'not_found') return ptBR.analise.aprovarParecerErroNotFound;
  if (reason === 'not_completed') return ptBR.analise.aprovarParecerErroNotCompleted;
  if (reason === 'already_approved') return ptBR.analise.aprovarParecerErroAlreadyApproved;
  if (reason === 'network') return ptBR.analise.aprovarParecerErroNetwork;
  return ptBR.analise.aprovarParecerErroGeneric;
}

// Formato fixo "dd/mm/aaaa hh:mm" em UTC — escolha intencional para testes
// determinísticos (toLocaleString varia com TZ do host); para a demo MVP o
// horário do servidor (UTC) é o que importa.
function formatApprovedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  const dd = pad(d.getUTCDate());
  const mm = pad(d.getUTCMonth() + 1);
  const yyyy = d.getUTCFullYear();
  const hh = pad(d.getUTCHours());
  const mi = pad(d.getUTCMinutes());
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

function ApprovalConfirmation({
  approvedAt,
  approvedByEmail,
}: {
  approvedAt: string;
  approvedByEmail: string | null;
}) {
  const formatted = formatApprovedAt(approvedAt);
  // E4.F2a.C04 — preferimos a mensagem com o email do perito; fallback para
  // a mensagem sem identidade só se o backend não tiver conseguido resolver
  // (ex.: usuário removido entre aprovação e leitura).
  const message =
    approvedByEmail !== null && approvedByEmail !== ''
      ? ptBR.analise.aprovacaoConfirmadaMensagem
          .replace('{{quando}}', formatted)
          .replace('{{perito}}', approvedByEmail)
      : ptBR.analise.aprovacaoConfirmadaMensagemSemPerito.replace('{{quando}}', formatted);
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="analise-aprovado"
      className="mt-sc-3 border border-success bg-success-50 px-sc-3 py-2 text-body-sm text-success-700"
    >
      <p className="font-semibold">{ptBR.analise.aprovacaoConfirmadaTitulo}</p>
      <p data-testid="analise-aprovado-mensagem">{message}</p>
    </div>
  );
}

export function ApprovalSection({ record }: { record: AnalysisRecordData }) {
  const apiClient = useApiClient();
  const [approval, setApproval] = useState<AnalysisApprovalData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorReason, setErrorReason] = useState<AnalysisErrorReason | null>(null);

  const approvedAt = approval?.approved_at ?? record.approved_at ?? null;
  const approvedByEmail = approval?.approved_by_email ?? record.approved_by_email ?? null;
  const alreadyApproved = approvedAt !== null;

  const handleApprove = useCallback(async () => {
    if (alreadyApproved || submitting) return;
    setSubmitting(true);
    setErrorReason(null);
    try {
      const data = await approveAnalysis(apiClient, record.id);
      setApproval(data);
    } catch (err) {
      setErrorReason(err instanceof AnalysisError ? err.reason : 'unknown');
    } finally {
      setSubmitting(false);
    }
  }, [apiClient, record.id, alreadyApproved, submitting]);

  const buttonLabel = submitting
    ? ptBR.analise.aprovarParecerEnviando
    : ptBR.analise.aprovarParecer;

  return (
    <Blueprint as="section" className="mt-sc-8 p-sc-4" aria-labelledby="aprovacao-title">
      <CardKicker className="block">{ptBR.analise.aprovarParecerKicker}</CardKicker>
      <h2 id="aprovacao-title" className="mt-1 text-heading-sm text-fg">
        {ptBR.analise.aprovarParecer}
      </h2>
      {alreadyApproved && approvedAt !== null ? (
        <ApprovalConfirmation approvedAt={approvedAt} approvedByEmail={approvedByEmail} />
      ) : (
        <>
          <p className="mt-1 text-body-sm text-fg-muted">{ptBR.analise.aprovarParecerDescricao}</p>
          {/* Aprovar o parecer é o ato final da tela: único objeto sólido. */}
          <Button
            type="button"
            onClick={() => void handleApprove()}
            disabled={submitting}
            aria-disabled={submitting}
            className="mt-sc-3"
            data-testid="analise-aprovar"
          >
            {buttonLabel}
          </Button>
        </>
      )}
      {errorReason !== null ? (
        <p
          className="mt-sc-2 border border-danger bg-danger-50 px-sc-3 py-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="analise-aprovar-erro"
        >
          {approvalErrorMessage(errorReason)}
        </p>
      ) : null}
    </Blueprint>
  );
}
